import {
  type DefaultOptions,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { wipePersistedCache } from "./query-persister";

const noBackgroundSync = process.env.NEXT_PUBLIC_NO_BACKGROUND_SYNC === "true";

function logQueryError(
  error: Error,
  query: { queryKey: readonly unknown[]; meta?: Record<string, unknown> },
) {
  // Per-query opt-out — set meta.suppressLog when the failure is expected
  // (e.g. admin-only endpoints fetched from non-admin sessions). Keeps the
  // console clean for routine permission gaps without hiding real errors.
  if (query.meta?.suppressLog) return;
  const key = JSON.stringify(query.queryKey);
  console.error(`[QueryClient] Query failed [${key}]:`, error.message);
}

const serverDefaults: DefaultOptions = {
  queries: {
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  },
  mutations: { retry: 0 },
};

const browserDefaults: DefaultOptions = {
  queries: {
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    refetchOnMount: noBackgroundSync ? false : "always",
    refetchOnWindowFocus: !noBackgroundSync,
    refetchInterval: noBackgroundSync ? false : 30_000,
  },
  mutations: { retry: 0 },
};

let browserQueryClient: QueryClient | undefined;

/**
 * Clear all cached queries from the browser-side QueryClient.
 *
 * Call on sign-out AND on involuntary 401 (token expiry) so switching tenants
 * or re-authenticating never exposes a previous tenant's cached data.
 *
 * No-ops on the server (where browserQueryClient is always undefined).
 */
export function clearQueryCache(): void {
  browserQueryClient?.clear();
}

/** Minimum gap between two full scrubs. Exported so tests can step past it. */
export const NUKE_MIN_INTERVAL_MS = 15_000;
let lastNukeAt = 0;

/**
 * H2-2125 — the full scrub, for when local state is KNOWN to be wrong.
 *
 * `clearQueryCache()` empties memory and leaves the IndexedDB blob behind for
 * the next load to restore. That is fine for sign-out (where a hard navigation
 * follows) but useless for a live desync: the whole point is to stop rendering
 * something the server never said. So this one:
 *
 *   1. deletes the persisted blob, so a reload cannot resurrect the bad state;
 *   2. drops every INACTIVE query outright — nothing is watching them, and a
 *      cached-but-wrong answer is worse than a refetch when they mount;
 *   3. RESETS every ACTIVE query, which discards its data and immediately
 *      refetches from the server. `invalidateQueries` would only mark them
 *      stale and keep painting the poisoned data until the refetch lands,
 *      which is exactly the frame a stuck spinner or a ghost message lives in.
 *
 * Rate-limited: a desync signal often fires several times in a row (a stall
 * watchdog re-arming, three components noticing the same bad row), and the
 * cure must not become a refetch storm. Subsequent calls inside the window are
 * dropped and logged rather than queued.
 *
 * `reason` is required and goes to the console verbatim. A cache that silently
 * heals itself is a cache whose corruption nobody can debug — whoever sees the
 * next occurrence needs to know which detector fired.
 */
export async function nukeClientCache(reason: string): Promise<boolean> {
  const client = browserQueryClient;
  if (!client) return false;
  const now = Date.now();
  if (now - lastNukeAt < NUKE_MIN_INTERVAL_MS) {
    console.warn(
      `[QueryClient] cache nuke suppressed (within ${NUKE_MIN_INTERVAL_MS}ms of the last one): ${reason}`,
    );
    return false;
  }
  lastNukeAt = now;
  console.warn(`[QueryClient] wiping client cache — ${reason}`);
  await wipePersistedCache();
  client.removeQueries({ type: "inactive" });
  await client.resetQueries({ type: "active" });
  return true;
}

export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    return new QueryClient({
      defaultOptions: serverDefaults,
      queryCache: new QueryCache({ onError: logQueryError }),
    });
  }
  if (!browserQueryClient) {
    browserQueryClient = new QueryClient({
      defaultOptions: browserDefaults,
      queryCache: new QueryCache({ onError: logQueryError }),
    });
  }
  return browserQueryClient;
}
